import {gql} from '@apollo/client';
import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import produce from 'immer';
import React, {useCallback, useEffect, useState} from 'react';
import {v4 as uuidv4} from 'uuid';

import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import ExportAuditLogButton from 'audit/ExportAuditLogButton';
import * as BenchlingUtil from 'BenchlingUtil';
import {setWindowTitle} from 'BenchlingUtil';
import {EntityStoreUser} from 'constants/EntityStoreConstants';
import {
  ReviewerType,
  ReviewLifecycleActionType,
  ReviewLifecycleTerminalState,
  ReviewLifecycleType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  OrganizationsTeamQuery,
  OrganizationsTeamQueryVariables,
} from 'notebook/reviews/review-lifecycles/__generated/OrganizationsTeamQuery';
import notifications from 'notifications';
import SettingsPageContainer from 'settings/SettingsPageContainer';
import EntityStore from 'stores/EntityStore';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import useOnMount from 'ui-library/hooks/useOnMount';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {OptionData} from 'ui-library/menus/MenuConstants';
import runModal from 'ui-library/runModal';
import SettingsField, {SettingsLabel} from 'ui-library/SettingsField';
import {CenteredSpinner} from 'ui-library/Spinner';
import APIUtil from 'util/APIUtil';
import {omit} from 'util/omitKey';
import {withLoadingScreen} from 'util/Promises';

import {
  ReviewLifecycleConfigurationQuery,
  ReviewLifecycleConfigurationQueryVariables,
} from './__generated/ReviewLifecycleConfigurationQuery';
import {onReviewLifecyclesArchive, onReviewLifecyclesUnarchive} from './lib';
import ReviewLifecycleConfigurationField, {DisabledRenderType} from './ReviewLifecycleConfigurationField';
import {
  ACTION_LABEL_OPTION_DATAS,
  COMPLETION_STATUS_OPTION_DATAS,
  MULTI_STAGE_REVIEW_TYPES,
  REVIEWER_TYPE_OPTION_DATAS,
  REVIEWER_TYPE_TO_DISPLAY_TEXT,
  ReviewLifecycleStage,
  SELF_REVIEW_STAGE_DESCRIPTION,
  SELF_REVIEW_STAGE_NAME,
} from './ReviewLifecycleConstants';
import * as ReviewLifecycleDAO from './ReviewLifecyclesDAO';
import {reviewLifecycleURL} from './ReviewLifecyclesDAO';
import {REVIEW_LIFECYCLE_TYPE_TO_DISPLAY_TEXT} from './ReviewLifecyclesListing';
import {areTeamReviewsEnabled, isAuthorReviewInReviewLifecyclesEnabled} from './ReviewLifecyclesUtil';
import ReviewLifecycleUpdateConfirmationModal from './ReviewLifecycleUpdateConfirmationModal';
import {ReviewLifecycleStageNode, ReviewStageTable} from './ReviewStageTable';
import WithReviewProcessHelpLink, {ReviewLifecycleHelpArticle} from './WithReviewLifecycleHelpLink';

type OrganizationNode = ExtractByTypename<
  OrganizationsTeamQuery['organizations'][number],
  'OrganizationNode'
>;

type Team = OrganizationNode['teams'][number];

type ReviewLifecycleGraphQL = ExtractByTypename<
  ReviewLifecycleConfigurationQuery['reviewLifecycle'],
  'ReviewLifecycleNode'
>;

type Stage = ReviewLifecycleGraphQL['currentVersion']['reviewStages'][number];

interface CreateReviewLifecycleProps {
  organizations: Array<OrganizationNode>;
  reviewLifecycle?: never;
  refetch?: () => Promise<void>;
}

interface EditReviewLifecycleProps {
  reviewLifecycle: ReviewLifecycleGraphQL;
  organizations?: never;
  refetch?: () => Promise<void>;
}

export type ReviewLifecycleConfigurationEditorProps = CreateReviewLifecycleProps | EditReviewLifecycleProps;

interface CurrentState {
  reviewLifecycleName: string;
  reviewLifecycleType: ReviewLifecycleType;
  completionStatus: ReviewLifecycleTerminalState;
  stageNodes: Array<ReviewLifecycleStageNode>;
}

export const ORDERED_REVIEW_LIFECYCLE_TYPES = [
  ReviewLifecycleType.PARALLEL,
  ReviewLifecycleType.SEQUENTIAL,
  ReviewLifecycleType.SELF_REVIEW,
];

export const REVIEW_LIFECYCLE_CONFIGURATION_QUERY = gql`
  query ReviewLifecycleConfigurationQuery($reviewLifecycleId: StrictString!)
  @skipAudit
  @engComponent(name: NOTEBOOK) {
    reviewLifecycle(id: $reviewLifecycleId) {
      ... on ReviewLifecycleNode {
        id
        archiveRecord {
          purpose
        }
        numProjectsUsingReviewLifecycle
        organization {
          id
          name
          admin
          teams {
            id
            name
          }
        }
        currentVersion {
          id
          name
          lifecycleType
          terminalState
          reviewStages {
            id
            name
            action
            reviewerType
            description
            selectedTeam {
              id
              name
            }
            isTeamReassignable
            allowAuthorReviewers
            allowSenderReviewer
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const ORGANIZATIONS_TEAM_QUERY = gql`
  query OrganizationsTeamQuery($organizationIds: [StrictString!]!) @skipAudit @engComponent(name: NOTEBOOK) {
    organizations(ids: $organizationIds) {
      ... on OrganizationNode {
        id
        name
        teams {
          id
          name
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function onBeforeUnloadHandler(e: BeforeUnloadEvent): void {
  e.preventDefault();
  e.returnValue = '';
}

function getReviewerTypeOptionDatas(teams: Array<Team>): Array<OptionData> {
  const options = REVIEWER_TYPE_OPTION_DATAS.filter(
    (opt) =>
      (opt.value !== ReviewerType.AUTHOR || isAuthorReviewInReviewLifecyclesEnabled()) &&
      opt.value !== ReviewerType.TEAM
  );
  if (areTeamReviewsEnabled() && teams.length > 0) {
    options.push({name: 'Teams', isHeader: true, className: css(styles.dropdownOptionWithDivider)});
    options.push(
      ...teams.map((team) => ({
        value: team.id,
        name: team.name,
      }))
    );
  }
  return options;
}

function getReviewTypeOptions(): Array<OptionData> {
  const allOptionDatas: Array<OptionData> = ORDERED_REVIEW_LIFECYCLE_TYPES.map(
    (type: ReviewLifecycleType) => ({
      value: type,
      name: REVIEW_LIFECYCLE_TYPE_TO_DISPLAY_TEXT[type],
      // Show divider for self review option
      className: type === ReviewLifecycleType.SELF_REVIEW ? css(styles.dropdownOptionWithDivider) : undefined,
    })
  );

  if (isAuthorReviewInReviewLifecyclesEnabled()) {
    return allOptionDatas;
  }

  return allOptionDatas.filter((opt) => opt.value !== ReviewLifecycleType.SELF_REVIEW);
}

export const createBlankStageNode = (reviewerOptions: Array<OptionData>): ReviewLifecycleStageNode => {
  const reviewerType = reviewerOptions.filter((opt) => opt.value === ReviewerType.PROJECT_MEMBERS)[0];
  const isAuthor = reviewerType.value === ReviewerType.AUTHOR;

  return {
    name: '',
    reviewerType,
    isTeamReassignable: false,
    allowAuthorReviewers: isAuthor, // True for AUTHOR type, false for other types
    allowSenderReviewer: false, // Default to false for all types
    actionLabel: ACTION_LABEL_OPTION_DATAS.filter((opt) => opt.value === ReviewLifecycleActionType.ACCEPT)[0],
    definition: '',
    clientId: uuidv4(),
    previousVersionReviewLifecycleStageId: null,
  } as ReviewLifecycleStageNode;
};

function getInitialReviewTypeOptionData(
  reviewLifecycle: ReviewLifecycleGraphQL | undefined,
  options: Array<OptionData>
): OptionData {
  return (
    options.filter((opt) => opt.value === reviewLifecycle?.currentVersion.lifecycleType)[0] ??
    options.filter((opt) => opt.value === ReviewLifecycleType.SEQUENTIAL)[0]
  );
}

function getInitialCompletionStatus(reviewLifecycle: ReviewLifecycleGraphQL | undefined): OptionData {
  return (
    COMPLETION_STATUS_OPTION_DATAS.filter(
      (opt) => opt.value === reviewLifecycle?.currentVersion.terminalState
    )[0] ??
    COMPLETION_STATUS_OPTION_DATAS.filter((opt) => opt.value === ReviewLifecycleTerminalState.ACCEPTED)[0]
  );
}

function findMatchingReviewer(reviewerTypeOptions: Array<OptionData>, reviewStage: Stage): OptionData {
  if (reviewStage.reviewerType === ReviewerType.TEAM) {
    return reviewerTypeOptions.filter((opt) => opt.value === reviewStage.selectedTeam?.id)[0];
  }

  return reviewerTypeOptions.filter((opt) => opt.value === reviewStage.reviewerType)[0];
}

function generateInitialStageNodes(
  selectedReviewType: ReviewLifecycleType,
  reviewLifecycle: ReviewLifecycleGraphQL | undefined,
  reviewerTypeOptions: Array<OptionData>
): Array<ReviewLifecycleStageNode> {
  if (
    reviewLifecycle?.currentVersion.lifecycleType &&
    (selectedReviewType === reviewLifecycle.currentVersion.lifecycleType ||
      // If selected and initial review types are both multi-stage types, then preserve stages from the
      // initial review lifecycle
      (MULTI_STAGE_REVIEW_TYPES.includes(reviewLifecycle?.currentVersion.lifecycleType) &&
        MULTI_STAGE_REVIEW_TYPES.includes(selectedReviewType)))
  ) {
    return reviewLifecycle.currentVersion.reviewStages.map(
      (reviewStage) =>
        ({
          name: reviewStage.name,
          reviewerType: findMatchingReviewer(reviewerTypeOptions, reviewStage),
          isTeamReassignable: reviewStage.isTeamReassignable ?? false,
          // Use actual backend values (these fields are non-nullable in GraphQL schema)
          allowAuthorReviewers: reviewStage.allowAuthorReviewers,
          allowSenderReviewer: reviewStage.allowSenderReviewer,
          actionLabel: ACTION_LABEL_OPTION_DATAS.filter((opt) => opt.value === reviewStage.action)[0],
          definition: reviewStage.description,
          clientId: uuidv4(),
          previousVersionReviewLifecycleStageId: reviewStage.id,
        }) as ReviewLifecycleStageNode
    );
  }
  return [createBlankStageNode(reviewerTypeOptions)];
}

function isSameTeamReviewerType(currentStageNode: ReviewLifecycleStageNode, initialStage: Stage): boolean {
  return (
    initialStage.reviewerType === ReviewerType.TEAM &&
    Boolean(initialStage.selectedTeam) &&
    currentStageNode.reviewerType.name === initialStage.selectedTeam!.name
  );
}

function hasReviewLifecycleBeenEdited(
  {
    reviewLifecycleName: currentReviewLifecycleName,
    reviewLifecycleType: currentReviewLifecycleType,
    completionStatus: currentCompletionStatus,
    stageNodes: currentStageNodes,
  }: CurrentState,
  {currentVersion: initialReviewLifecycleVersion}: ReviewLifecycleGraphQL
): boolean {
  const reviewStages = initialReviewLifecycleVersion.reviewStages;

  if (
    currentReviewLifecycleName !== initialReviewLifecycleVersion.name ||
    currentReviewLifecycleType !== initialReviewLifecycleVersion.lifecycleType ||
    currentCompletionStatus !== initialReviewLifecycleVersion.terminalState
  ) {
    return true;
  }

  if (currentStageNodes.length !== reviewStages.length) {
    return true;
  }

  return (
    currentStageNodes.find((stageNode, i) => {
      const isDifferentName = stageNode.name !== reviewStages[i].name;
      const isDifferentReviewerType =
        stageNode.reviewerType &&
        stageNode.reviewerType.value !== reviewStages[i].reviewerType &&
        !isSameTeamReviewerType(stageNode, reviewStages[i]);
      const isDifferentDescription = stageNode.definition !== reviewStages[i].description;
      const isDifferentAction = stageNode.actionLabel.value !== reviewStages[i].action;
      const isDifferentTeamReassignable = stageNode.isTeamReassignable !== reviewStages[i].isTeamReassignable;

      // Compare against actual backend values (non-nullable in GraphQL schema)
      const isDifferentAllowAuthorReviewers =
        stageNode.allowAuthorReviewers !== reviewStages[i].allowAuthorReviewers;
      const isDifferentAllowSenderReviewer =
        stageNode.allowSenderReviewer !== reviewStages[i].allowSenderReviewer;

      return (
        isDifferentName ||
        isDifferentReviewerType ||
        isDifferentDescription ||
        isDifferentAction ||
        isDifferentTeamReassignable ||
        isDifferentAllowAuthorReviewers ||
        isDifferentAllowSenderReviewer
      );
    }) != null
  );
}

const hasRequiredFields = ({
  reviewLifecycleName: currentReviewLifecycleName,
  reviewLifecycleType: currentReviewLifecycleType,
  stageNodes: currentStageNodes,
}: CurrentState): boolean => {
  if (currentReviewLifecycleType === ReviewLifecycleType.SELF_REVIEW) {
    return currentReviewLifecycleName.trim().length > 0 && currentStageNodes.length === 1;
  }
  return (
    !currentStageNodes.some((stageNode) => stageNode.name.trim().length === 0) &&
    currentReviewLifecycleName.trim().length > 0 &&
    currentStageNodes.length > 0
  );
};

function shouldEnableSaveButton(
  reviewLifecycle: ReviewLifecycleGraphQL | undefined,
  currentState: CurrentState
): boolean {
  if (!hasRequiredFields(currentState)) {
    return false;
  }
  if (reviewLifecycle) {
    return hasReviewLifecycleBeenEdited(currentState, reviewLifecycle);
  }
  return true;
}

function navigateToReviewLifecycleListing(): void {
  BenchlingUtil.navigate(null, ReviewLifecycleDAO.reviewLifecyclesListingURL());
}

function ReviewLifecycleConfigurationEditor({
  reviewLifecycle,
  organizations,
  refetch,
}: ReviewLifecycleConfigurationEditorProps): JSX.Element {
  useOnMount(() => {
    setWindowTitle('Review Process Editor');
  });
  let organizationMenuOptions;
  // Editing can only be disabled when viewing an existing review lifecycle, so we initialize as false, and
  // set to true if we are editing an existing review lifecycle, and user is not admin.
  let areEditsDisabled = false;
  if (reviewLifecycle == null) {
    assert(organizations != null, '');
    organizationMenuOptions = organizations.map(
      (org) =>
        ({
          value: org.id,
          name: org.name,
        }) as OptionData
    );
    assert(organizationMenuOptions.length > 0, '');
  } else {
    areEditsDisabled = !reviewLifecycle.organization.admin || reviewLifecycle.archiveRecord != null;
    organizationMenuOptions = [
      {
        value: reviewLifecycle.organization.id,
        name: reviewLifecycle.organization.name,
      },
    ];
  }

  const [organization, setOrganization] = useState(
    organizationMenuOptions.filter((opt) => opt.value === reviewLifecycle?.organization.id)[0] ??
      organizationMenuOptions[0]
  );
  const [reviewLifecycleName, setReviewLifecycleName] = useState(reviewLifecycle?.currentVersion.name ?? '');

  const reviewTypeOptions = getReviewTypeOptions();
  const [reviewType, _setReviewType] = useState(
    getInitialReviewTypeOptionData(reviewLifecycle, reviewTypeOptions)
  );

  const [completionStatus, setCompletionStatus] = useState(getInitialCompletionStatus(reviewLifecycle));

  const teams =
    reviewLifecycle != null
      ? reviewLifecycle.organization.teams
      : organizations?.filter((org) => org.id === organization.value)[0].teams;
  const reviewerTypeOptionDatas = getReviewerTypeOptionDatas(teams ?? []);
  const [stageNodes, setStageNodes] = useState<Array<ReviewLifecycleStageNode>>(
    generateInitialStageNodes(reviewType.value, reviewLifecycle, reviewerTypeOptionDatas)
  );

  const getCurrentState = useCallback(
    () => ({
      reviewLifecycleName,
      reviewLifecycleType: reviewType.value,
      completionStatus: completionStatus.value,
      stageNodes,
    }),
    [reviewLifecycleName, reviewType.value, completionStatus, stageNodes]
  );

  useEffect(() => {
    if (shouldEnableSaveButton(reviewLifecycle, getCurrentState())) {
      window.addEventListener('beforeunload', onBeforeUnloadHandler);
    } else {
      window.removeEventListener('beforeunload', onBeforeUnloadHandler);
    }
  }, [reviewLifecycle, getCurrentState]);

  function setReviewType(reviewTypeOption: OptionData): void {
    _setReviewType(reviewTypeOption);
    if ([reviewType.value, reviewTypeOption.value].includes(ReviewLifecycleType.SELF_REVIEW)) {
      // When toggling to / from self-review, since the view completely changes, we re-initialize the stages.
      // If we are switching away from self-review, we re-build the stages using the existing review
      // lifecycle, if it exists.
      setStageNodes(
        generateInitialStageNodes(reviewTypeOption.value, reviewLifecycle, reviewerTypeOptionDatas)
      );
    }
  }

  const reorderStages = (newOrderedClientIds: Array<string>): void => {
    setStageNodes(
      newOrderedClientIds.map(
        (clientId: string) =>
          stageNodes.find((stageNode: ReviewLifecycleStageNode) => stageNode.clientId === clientId)!
      )
    );
  };

  function setActionLabel(
    stageNodes: Array<ReviewLifecycleStageNode>,
    idx: number,
    actionLabel: OptionData
  ): void {
    setStageNodes(
      produce(stageNodes, (draftStageNodes) => {
        draftStageNodes[idx].actionLabel = actionLabel;
      })
    );
  }

  function setReviewerType(
    stageNodes: Array<ReviewLifecycleStageNode>,
    idx: number,
    reviewerType: OptionData
  ): void {
    setStageNodes(
      produce(stageNodes, (draftStageNodes) => {
        draftStageNodes[idx].reviewerType = reviewerType;

        // Will stay checked if it was checked before.
        draftStageNodes[idx].allowSenderReviewer = stageNodes[idx].allowSenderReviewer;

        // If the reviewer type goes from team to non-team, then isTeamReassignable should be reset to false.
        // Otherwise, it should remain the same.
        draftStageNodes[idx].isTeamReassignable =
          APIUtil.isTeam(reviewerType.value) && stageNodes[idx].isTeamReassignable;

        // If the reviewer type is author, it must be set to true. Otherwise, it should remain the same.
        draftStageNodes[idx].allowAuthorReviewers =
          reviewerType.value === ReviewerType.AUTHOR || stageNodes[idx].allowAuthorReviewers;
      })
    );
  }

  function setStageName(stageNodes: Array<ReviewLifecycleStageNode>, idx: number, name: string): void {
    setStageNodes(
      produce(stageNodes, (draftStageNodes) => {
        draftStageNodes[idx].name = name;
      })
    );
  }

  function setIsTeamReassignable(
    stageNodes: Array<ReviewLifecycleStageNode>,
    idx: number,
    isTeamReassignable: boolean
  ): void {
    setStageNodes(
      produce(stageNodes, (draftStageNodes) => {
        draftStageNodes[idx].isTeamReassignable = isTeamReassignable;
      })
    );
  }

  function setAllowAuthorReviewers(
    stageNodes: Array<ReviewLifecycleStageNode>,
    idx: number,
    allowAuthorReviewers: boolean
  ): void {
    setStageNodes(
      produce(stageNodes, (draftStageNodes) => {
        draftStageNodes[idx].allowAuthorReviewers = allowAuthorReviewers;
      })
    );
  }

  function setAllowSenderReviewer(
    stageNodes: Array<ReviewLifecycleStageNode>,
    idx: number,
    allowSenderReviewer: boolean
  ): void {
    setStageNodes(
      produce(stageNodes, (draftStageNodes) => {
        draftStageNodes[idx].allowSenderReviewer = allowSenderReviewer;
      })
    );
  }

  function setDefinition(stageNodes: Array<ReviewLifecycleStageNode>, idx: number, definition: string): void {
    setStageNodes(
      produce(stageNodes, (draftStageNodes) => {
        draftStageNodes[idx].definition = definition;
      })
    );
  }

  function removeReviewStage(stageNodes: Array<ReviewLifecycleStageNode>, idx: number): void {
    setStageNodes(
      produce(stageNodes, (draftStageNodes) => {
        draftStageNodes.splice(idx, 1);
      })
    );
  }

  const handleSubmit = async (): Promise<void> => {
    let reviewStages: Array<ReviewLifecycleStage>;
    if (reviewType.value === ReviewLifecycleType.SELF_REVIEW) {
      reviewStages = [
        {
          description: SELF_REVIEW_STAGE_DESCRIPTION,
          name: SELF_REVIEW_STAGE_NAME,
          action: stageNodes[0].actionLabel.value,
          reviewerType: ReviewerType.AUTHOR,
          isTeamReassignable: false,
          allowAuthorReviewers: true, // Always true for AUTHOR stages
          allowSenderReviewer: stageNodes[0].allowSenderReviewer,
          previousVersionReviewLifecycleStageId: stageNodes[0].previousVersionReviewLifecycleStageId,
        },
      ];
    } else {
      reviewStages = stageNodes.map((stageNode: ReviewLifecycleStageNode): ReviewLifecycleStage => {
        const reviewerType: ReviewerType = APIUtil.isTeam(stageNode.reviewerType.value)
          ? ReviewerType.TEAM
          : stageNode.reviewerType.value;

        // AUTHOR type always has allowAuthorReviewers set to true
        const allowAuthorReviewers =
          reviewerType === ReviewerType.AUTHOR ? true : stageNode.allowAuthorReviewers;
        const allowSenderReviewer = stageNode.allowSenderReviewer;

        return {
          action: stageNode.actionLabel.value,
          description: stageNode.definition === '' ? null : stageNode.definition,
          name: stageNode.name,
          reviewerType,
          isTeamReassignable: stageNode.isTeamReassignable,
          allowAuthorReviewers,
          allowSenderReviewer,
          ...(reviewerType === ReviewerType.TEAM ? {selectedTeamId: stageNode.reviewerType.value} : {}),
          previousVersionReviewLifecycleStageId: stageNode.previousVersionReviewLifecycleStageId,
        };
      });
    }

    async function updateReviewLifecycleAndRefetch(): Promise<void> {
      await ReviewLifecycleDAO.updateReviewLifecycle({
        lifecycleType: reviewType.value,
        name: reviewLifecycleName,
        terminalState: completionStatus.value,
        reviewStages,
        reviewLifecycleId: reviewLifecycle!.id,
      });
      if (refetch) {
        await refetch();
      }
    }

    if (reviewLifecycle) {
      const onRequestSubmit = async (): Promise<void> => {
        window.onbeforeunload = () => '';
        await withLoadingScreen(updateReviewLifecycleAndRefetch());
        window.onbeforeunload = null;
      };

      if (reviewLifecycle.numProjectsUsingReviewLifecycle > 0) {
        runModal(ReviewLifecycleUpdateConfirmationModal, {
          reviewLifecycleName: reviewLifecycle.currentVersion.name,
          numProjectsUsingReviewLifecycle: reviewLifecycle.numProjectsUsingReviewLifecycle,
          onRequestSubmit,
        });
      } else {
        // Skip confirmation modal if there are 0 projects to update.
        await onRequestSubmit();
      }
    } else {
      const reviewLifecycleResponse = await ReviewLifecycleDAO.createReviewLifecycle({
        lifecycleType: reviewType.value,
        name: reviewLifecycleName,
        terminalState: completionStatus.value,
        reviewStages: reviewStages.map((stage) => omit(stage, 'previousVersionReviewLifecycleStageId')),
        organizationId: organization.value,
      });
      BenchlingUtil.navigate(null, reviewLifecycleURL(reviewLifecycleResponse.id));
      window.removeEventListener('beforeunload', onBeforeUnloadHandler);
      notifications.success(`Created review process successfully.`);
    }
  };

  const renderReviewStageInput = (): JSX.Element => {
    if (reviewType.value === ReviewLifecycleType.SELF_REVIEW) {
      return (
        <div className={css(styles.rowSection)}>
          <div className={css(styles.columnSection)}>
            <div className={css(styles.metadataField)}>
              <ReviewLifecycleConfigurationField
                id="reviewerDropdown"
                className={css(styles.fields)}
                dropdownLabel="Reviewer type"
                optionDatas={reviewerTypeOptionDatas}
                selectedOption={{
                  value: ReviewerType.AUTHOR,
                  name: REVIEWER_TYPE_TO_DISPLAY_TEXT[ReviewerType.AUTHOR],
                }}
                handleSelectionChange={noop}
                disabled={true}
                // When rendering a Self-Review config, the reviewer type is always disabled. However,
                // we choose to render text if the user doesn't have permissions to edit anything,
                // and a disabled dropdown if they do to match the surrounding UI.
                disabledRenderType={areEditsDisabled ? DisabledRenderType.TEXT : DisabledRenderType.DROPDOWN}
              />
            </div>
          </div>
          <div className={css(styles.columnSection)}>
            <div className={css(styles.metadataField)}>
              <ReviewLifecycleConfigurationField
                id="actionLabelDropdown"
                className={css(styles.fields)}
                dropdownLabel="Action label"
                optionDatas={ACTION_LABEL_OPTION_DATAS}
                selectedOption={stageNodes[0].actionLabel}
                handleSelectionChange={(actionLabelOption) => {
                  setStageNodes(
                    produce(stageNodes, (draftStageNodes) => {
                      draftStageNodes[0].actionLabel = actionLabelOption;
                    })
                  );
                }}
                help="The button label a reviewer will click when completing the stage."
                disabled={areEditsDisabled}
                disabledRenderType={DisabledRenderType.TEXT}
              />
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className={css(styles.tableWrapper)}>
        <ReviewStageTable
          stageNodes={stageNodes}
          onUpdateReviewStages={setStageNodes}
          setName={setStageName}
          removeReviewStage={removeReviewStage}
          setIsTeamReassignable={setIsTeamReassignable}
          setAllowAuthorReviewers={setAllowAuthorReviewers}
          setAllowSenderReviewer={setAllowSenderReviewer}
          setDefinition={setDefinition}
          setReviewerType={setReviewerType}
          setActionLabel={setActionLabel}
          reorderStages={reorderStages}
          actionLabelsMenuOptions={ACTION_LABEL_OPTION_DATAS}
          reviewerTypeMenuOptions={reviewerTypeOptionDatas}
          dataTestAttrs={{
            component: 'ReviewLifecycleConfigurationEditor',
            element: 'reviewStageTable',
          }}
          disabled={areEditsDisabled}
        />
      </div>
    );
  };

  const saveButton = (
    <Button
      className={css(styles.marginLeft)}
      buttonStyle={ButtonStyle.SECONDARY}
      onClick={() => {
        handleSubmit();
      }}
      size={ButtonSize.MEDIUM}
      text="Save"
      disabled={!shouldEnableSaveButton(reviewLifecycle, getCurrentState())}
      dataTestAttrs={{
        component: 'ReviewLifecycleConfigurationEditor',
        element: 'save',
      }}
    />
  );

  return (
    <SettingsPageContainer
      title="Review Processes"
      onClickTitle={navigateToReviewLifecycleListing}
      toolbar={
        <>
          {reviewLifecycle &&
            !isArchived({...reviewLifecycle, name: reviewLifecycle.currentVersion.name}) && (
              <Tooltip text="Archive">
                <Button
                  buttonStyle={ButtonStyle.NEGATIVE}
                  className={css(styles.marginRight)}
                  disabled={areEditsDisabled}
                  icon="archive"
                  onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    onReviewLifecyclesArchive([reviewLifecycle], refetch!);
                  }}
                  size={ButtonSize.MEDIUM}
                />
              </Tooltip>
            )}
          {reviewLifecycle &&
            reviewLifecycle.organization.admin &&
            isArchived({...reviewLifecycle, name: reviewLifecycle.currentVersion.name}) && (
              <Tooltip text="Unarchive">
                <Button
                  buttonStyle={ButtonStyle.SECONDARY}
                  className={css(styles.marginRight)}
                  icon="unarchive"
                  onClick={async (event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    await onReviewLifecyclesUnarchive([reviewLifecycle], refetch!);
                  }}
                  size={ButtonSize.MEDIUM}
                />
              </Tooltip>
            )}
          {reviewLifecycle != null ? (
            <ExportAuditLogButton
              ids={[reviewLifecycle.id]}
              buttonSize={ButtonSize.MEDIUM}
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
            />
          ) : null}
          {!areEditsDisabled && saveButton}
        </>
      }
      subtitle={
        reviewLifecycle && areEditsDisabled
          ? reviewLifecycleName
          : `${reviewLifecycle ? 'Edit' : 'Create'} Review Process`
      }
    >
      <div className="u-flex-grow u-min-width-zero">
        <div className="settingsPageContainer-section" />
        <ReviewLifecycleConfigurationField
          id="ownerName"
          className={css(styles.fields, styles.organizationField)}
          dropdownLabel="Organization"
          optionDatas={organizationMenuOptions}
          selectedOption={organization}
          handleSelectionChange={(organizationOption: OptionData) => {
            setOrganization(organizationOption);
          }}
          help="Organization cannot be modified after review process creation."
          disabled={reviewLifecycle != null}
          disabledRenderType={areEditsDisabled ? DisabledRenderType.TEXT : DisabledRenderType.DROPDOWN}
        />

        <WithReviewProcessHelpLink helpArticle={ReviewLifecycleHelpArticle.CREATE_ENTRY_REVIEW_PROCESS}>
          Learn what a Review Process is
        </WithReviewProcessHelpLink>
        <div className={css(styles.rowSection)}>
          <div className={css(styles.columnSection)}>
            <div className={css(styles.metadataField)}>
              <ReviewLifecycleConfigurationField
                id="reviewType"
                className={css(styles.fields)}
                dropdownLabel="Review Type"
                optionDatas={reviewTypeOptions}
                selectedOption={reviewType}
                handleSelectionChange={(reviewTypeOption: OptionData) => {
                  assert(
                    reviewTypeOption.value !== ReviewLifecycleType.SELF_REVIEW ||
                      isAuthorReviewInReviewLifecyclesEnabled(),
                    'Self-reviews are not enabled'
                  );
                  setReviewType(reviewTypeOption);
                }}
                dataTestAttrs={{
                  component: 'ReviewLifecycleConfigurationEditor',
                  element: 'lifecycleTypeDropdown',
                }}
                disabled={areEditsDisabled}
                disabledRenderType={DisabledRenderType.TEXT}
              />
            </div>
          </div>
          <div className={css(styles.columnSection)}>
            <div className={css(styles.metadataField)}>
              {areEditsDisabled ? (
                <>
                  <SettingsLabel isRequired={false} label="Name" name="Name" />
                  <div>{reviewLifecycleName}</div>
                </>
              ) : (
                <SettingsField
                  isBeingCreated={true}
                  isWritable={true}
                  isRequired={true}
                  label="Name"
                  name="Name"
                  onChange={(name: string) => {
                    setReviewLifecycleName(name);
                  }}
                  value={reviewLifecycleName}
                  dataTestAttrs={{
                    component: 'ReviewLifecycleConfigurationEditor',
                    element: 'reviewLifecycleName',
                  }}
                />
              )}
            </div>
          </div>
        </div>
        {renderReviewStageInput()}
        <div className={css(styles.rowSection)}>
          <ReviewLifecycleConfigurationField
            id="completionStatus"
            className={css(styles.fields)}
            dropdownLabel="Completion status"
            optionDatas={COMPLETION_STATUS_OPTION_DATAS}
            selectedOption={completionStatus}
            handleSelectionChange={(completionStatusOption: OptionData) => {
              setCompletionStatus(completionStatusOption);
            }}
            disabled={areEditsDisabled}
            disabledRenderType={DisabledRenderType.TEXT}
            help="Defines the status of the entry once the review is complete."
          />
        </div>
      </div>
    </SettingsPageContainer>
  );
}

export interface QueryingReviewLifecycleConfigurationEditorProps {
  reviewLifecycleId?: string;
}

export default function QueryingReviewLifecycleConfigurationEditor({
  reviewLifecycleId,
}: QueryingReviewLifecycleConfigurationEditorProps): JSX.Element {
  const currentUser = useStoreSelector(
    EntityStore,
    useCallback((store) => store.currentUser(), [])
  );
  if (!currentUser) {
    return <CenteredSpinner />;
  }
  const organizations = (currentUser as EntityStoreUser).organizations ?? [];
  if (reviewLifecycleId) {
    return (
      <BenchlingQuery<ReviewLifecycleConfigurationQuery, ReviewLifecycleConfigurationQueryVariables>
        query={REVIEW_LIFECYCLE_CONFIGURATION_QUERY}
        variables={{reviewLifecycleId}}
        fetchPolicy="cache-and-network"
        renderLoading={() => <CenteredHorizontalThrobber />}
      >
        {({reviewLifecycle: reviewLifecycleOrInaccessible}, {refetch}) => {
          assert(
            !isInaccessible(reviewLifecycleOrInaccessible),
            'Expected review lifecycle data to have been loaded'
          );
          return (
            <ReviewLifecycleConfigurationEditor
              reviewLifecycle={reviewLifecycleOrInaccessible}
              refetch={async () => {
                await refetch();
              }}
            />
          );
        }}
      </BenchlingQuery>
    );
  }
  const orgsWhereUserIsAdmin = organizations?.filter((org) => org.admin) ?? [];

  if (orgsWhereUserIsAdmin.length === 0) {
    return (
      <SettingsPageContainer title="Review Processes" onClickTitle={navigateToReviewLifecycleListing}>
        <div>
          <h2>Access Required</h2>
          <p>
            You do not have access to this feature. Please contact your tenant admin for more information.
          </p>
        </div>
      </SettingsPageContainer>
    );
  }
  return (
    <BenchlingQuery<OrganizationsTeamQuery, OrganizationsTeamQueryVariables>
      query={ORGANIZATIONS_TEAM_QUERY}
      variables={{organizationIds: orgsWhereUserIsAdmin.map((org) => org.id)}}
      fetchPolicy="cache-and-network"
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {({organizations: organizationsOrInaccessible}) => {
        const organizations = organizationsOrInaccessible.filter(
          (organizationOrInaccessible): organizationOrInaccessible is OrganizationNode =>
            !isInaccessible(organizationOrInaccessible)
        );
        // User should have gotten a 404 if they did not have configuration permissions by this point.
        return <ReviewLifecycleConfigurationEditor organizations={organizations} />;
      }}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  fields: {
    width: 400,
  },
  tableWrapper: {
    marginTop: 40,
    width: '100%',
  },
  rowSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    width: '100%',
    marginTop: 40,
  },
  columnSection: {
    paddingRight: '20px',
  },
  metadataField: {
    width: 400,
    marginBottom: '0',
    paddingRight: '20px',
  },
  marginLeft: {
    marginLeft: '8px',
  },
  organizationField: {
    paddingBottom: '14px',
  },
  dropdownOptionWithDivider: {borderTop: `1px solid ${GRAY_LIGHT}`},
  marginRight: {
    marginRight: '8px',
  },
});
